/**
 * Detailed Person Search (Global Search)
 *
 * Overview:
 * - Renders a two-column layout: filters on the left and results on the right.
 * - Builds a normalized filter payload and dispatches a Redux action to retrieve people.
 * - Supports bulk actions via modals: send SMS/App messages and send emails by template.
 * - Integrates Ant Design components for layout and loading states.
 * - Consumes data from the Redux `driverReducer` to populate and update the person list.
 *
 * Primary child components:
 * - `Filters`: Captures user input and triggers search/reset.
 * - `FilterChips`: Displays applied filters with remove capability.
 * - `PersonList`: Shows search results and handles selection for bulk actions.
 * - `CandidateTableMessageModal`, `SendEmailByTemplateToPersonModal`: Action modals.
 *
 *  Wrapper component: DetailedPersonSearch
 *
 * Responsible for orchestrating filter application, dispatching searches,
 * rendering the results list, and managing action modals for bulk operations.
 *
 * @returns {JSX.Element} Detailed Person Search view container
 */

import { Col, Empty, Row, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import { FIELD_TYPES } from "../shared/constants";
import { SEND_SMS_MODAL_HEADER } from "../../constants/labels";
import dateTime from "@constants/dateTime";

import Loader from "@ui/feedback/loader";
import CandidateTableMessageModal from "@app/features/candidate-management/ui/candidate-management/candidate-table-message-modal";
import SendEmailByTemplateToPersonModal from "@ui/messaging/send-email-by-template-to-person-modal";
import {
  SEND_MESSAGE_TAB,
  SMS_HISTORY_TAB,
  TEMPLATE_TAB
} from "@ui/messaging/send-message-modal";
import Filters from "./filter";
import PersonList from "./person-list";
import FilterChips from "../shared/filter-chips";

import { detailPersonSearch } from "@redux/actionCreators";

const { TEXT_FIELD, SELECT_FIELD, SELECT_DATE_FIELD } = FIELD_TYPES;
const { DATE_FORMAT_STRING } = dateTime;
const MESSAGE_TAB_ORDER = [SEND_MESSAGE_TAB, TEMPLATE_TAB, SMS_HISTORY_TAB];

const defaultFilters = [
  {
    label: "STS",
    value: "false",
    filterId: "STS"
  }
];

const DetailedPersonSearch = () => {
  const dispatch = useDispatch();

  const [detailFilter, setDetailFilter] = useState(defaultFilters);
  const [modalState, setModalState] = useState({ isVisible: false, modalName: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const [tableData, setTableData] = useState([]);

  const driverReducer = useSelector(rdc => rdc?.driverReducer, shallowEqual);

  const {
    detailPersonSearchInfo: {
      resultData: detailPersonSearchData,
      isLoading: detailPersonSearchLoading
    }
  } = driverReducer;

  const sendRecipients = useMemo(() => {
    const ids = modalState.personObjectIds || [];
    return ids.map(id => {
      const person = tableData.find(item => item.ObjectId === id);
      return {
        objectId: id,
        name: person?.Name || "",
        candidateObjectId: person?.CandidateList?.[0]?.ObjectId
      };
    });
  }, [modalState.personObjectIds, tableData]);

  const openModal = ({ modalName, ...modalProps }) => {
    if (modalName === "SendSmsAndAppMessageModal") {
      // modalProps: personObjectIds[]
      setModalState({ isVisible: true, modalName, ...modalProps });
    } else if (modalName === "SendEmailByTemplateToPersonModal") {
      // modalProps: personObjectIds[]
      const selectedPersons = tableData.filter(item => selectedIds.includes(item.ObjectId));
      setModalState({ isVisible: true, modalName, selectedPersons });
    } else if (modalName === SEND_SMS_MODAL_HEADER) {
      // modalProps: { personObjectId, candidateObjectId, candidateName, width, type }
      setModalState({ isVisible: true, modalName, ...modalProps });
    }
  };

  // A multi-select add stores `values` (OR across them); `value` stays a display string.
  // A separate add of the same filter is a separate chip, which the backend ANDs.
  // `displayValue` is the human-readable text shown on the chip (names, not ids).
  const toValueFields = (dropDownValue, displayValue) =>
    Array.isArray(dropDownValue)
      ? { value: dropDownValue.join(", "), values: dropDownValue, displayValue }
      : { value: dropDownValue, displayValue };

  const handleFilterChange = filter => {
    const filterItems = [];
    if (filter.fieldType === SELECT_FIELD) {
      const filterData = {
        label: filter.filterName,
        ...toValueFields(filter.dropDownValue, filter.displayValue),
        filterId: filter.filterId
      };
      filterItems.push(filterData);
    } else if (filter.fieldType === TEXT_FIELD) {
      const textValue = filter.textValue;
      const isMultiText = Array.isArray(textValue) && textValue.length > 1;
      const filterData = {
        label: filter.filterName,
        ...(isMultiText
          ? toValueFields(textValue, textValue.join(", "))
          : { value: Array.isArray(textValue) ? textValue[0] : textValue }),
        filterId: filter.filterId
      };
      filterItems.push(filterData);
    } else if (filter.fieldType === SELECT_DATE_FIELD) {
      const filterData = {
        label: filter.filterName,
        ...toValueFields(filter.dropDownValue, filter.displayValue),
        dateValue: filter.dateValue,
        filterId: filter.filterId
      };
      filterItems.push(filterData);
    }
    setDetailFilter(previousFiles => [...previousFiles, ...filterItems]);
  };

  const removeFilter = index => {
    const newArray = detailFilter.filter((data, i) => i !== index);
    setDetailFilter(newArray);
    if (newArray.length === 0) {
      setTableData([]);
    }
  };

  const clearDetailFilter = () => {
    setDetailFilter([]);
    setTableData([]);
  };

  const selectAllIds = (e, data) => {
    const checkedValue = e.target?.checked;
    if (checkedValue) {
      const filteredIds = data.map(item => item.ObjectId);
      setSelectedIds(filteredIds);
    } else {
      setSelectedIds([]);
    }
  };

  const searchPerson = () => {
    const requestFiltersArray = detailFilter.map(filter => {
      return {
        filterId: filter.filterId,
        // `value` stays the first value so the BFF's required field is always present
        value: filter.values ? filter.values[0] : filter.value,
        ...(filter.values && { values: filter.values }),
        dateValue: filter.dateValue ? dayjs(filter.dateValue).format(DATE_FORMAT_STRING) : null
      };
    });

    dispatch(detailPersonSearch(requestFiltersArray));
  };

  const handleCheckbox = (e, item) => {
    const checkedValue = e.target?.checked;
    if (checkedValue) {
      setSelectedIds(previousFiles => [...previousFiles, item.ObjectId]);
    } else {
      const filteredValues = selectedIds.filter(data => data !== item.ObjectId);
      setSelectedIds(filteredValues);
    }
  };

  const closeModal = () => {
    setModalState({ isVisible: false, modalName: null });
  };

  // fill table state with detailPersonSearchData
  useEffect(() => {
    if (detailPersonSearchData) {
      setTableData(detailPersonSearchData?.Data || []);
      // select all persons
      const allIds = detailPersonSearchData?.Data?.map(item => item.ObjectId) || [];
      setSelectedIds(allIds);
    }
  }, [detailPersonSearchData]);

  return (
    <div className="global-search detailed-person-search-container">
      <Spin
        indicator={<Loader />}
        spinning={detailPersonSearchLoading}
        className="loaderGifPosition"
      >
        {modalState.modalName === "SendSmsAndAppMessageModal" && (
          <CandidateTableMessageModal
            recipients={sendRecipients}
            onClose={closeModal}
            onSent={() => setSelectedIds([])}
            defaultTab={SEND_MESSAGE_TAB}
            tabOrder={MESSAGE_TAB_ORDER}
          />
        )}

        {modalState.modalName === SEND_SMS_MODAL_HEADER && (
          <CandidateTableMessageModal
            personObjectId={modalState.personObjectId}
            candidateObjectId={modalState.candidateObjectId}
            candidateName={modalState.candidateName}
            onClose={closeModal}
            defaultTab={SEND_MESSAGE_TAB}
            tabOrder={MESSAGE_TAB_ORDER}
          />
        )}

        {/* send sms with templates */}
        {modalState.modalName === "SendEmailByTemplateToPersonModal" && (
          <SendEmailByTemplateToPersonModal
            isVisible={modalState.isVisible}
            selectedPersons={modalState.selectedPersons}
            resetModalFn={closeModal}
          />
        )}

        <Row>
          <Col span={6}>
            <Filters
              searchPerson={searchPerson}
              clearDetailFilter={clearDetailFilter}
              handleFilterChange={handleFilterChange}
              tableData={tableData}
              detailFilter={detailFilter}
            />
          </Col>

          <Col span={17} className="result-list ml-2">
            <FilterChips detailFilter={detailFilter} removeFilter={removeFilter} />

            {/* person list container */}
            {tableData.length !== 0 ? (
              <PersonList
                tableData={tableData}
                selectedIds={selectedIds}
                selectAllIds={selectAllIds}
                handleCheckbox={handleCheckbox}
                openModal={openModal}
              />
            ) : (
              <Empty />
            )}
          </Col>
        </Row>
      </Spin>
    </div>
  );
};
export default DetailedPersonSearch;
